import { FC, useContext, useMemo, useState } from 'react';
import { Button, Flex, Stack, Text } from '@chakra-ui/react';

import { UseOpenCloseReturn } from '~/core/hooks/useOpenClose';
import { buildIncludeParams } from '~/core/shared/api';
import { useApplication } from '~/core/shared/api/middle-office/application/hooks/useApplication/useApplication';
import { useApplicationGreenIdCheck } from '~/core/shared/api/middle-office/application/hooks/useApplicationGreenIdCheck/useApplicationGreenIdCheck';
import { Dialog } from '~/core/shared/components';
import { useTenantedApplicationConfig } from '~/core/shared/contexts';
import { useEventHandler } from '~/core/shared/hooks/useEventHandler';
import { Document, Entity } from '~/core/shared/types';
import { handleError, handleSuccess } from '~/core/shared/utils/admin';
import { getDocumentIncludesForRelationshipObject } from '~/core/shared/utils/queries';
import {
  ApplicationContext,
  DocumentDetailsViewActionContext,
} from '../Layout.component';

type PeopleArray = Document<
  | 'director'
  | 'shareholder'
  | 'individual_trustee'
  | 'sole_trader'
  | 'corporate_trustee_director'
  | 'partner'
>[];

// @TODO: this is Thorn-specific -> needs to be removed from core
export const GreenIdCheckModal: FC = () => {
  const config = useTenantedApplicationConfig();
  const {
    applicationUuid,
    definition,
    setLastApplicationUpdate,
    actionContexts,
  } = useContext(ApplicationContext);

  const {
    action: { key },
  } = useContext(DocumentDetailsViewActionContext);

  const greenIdCheckModalApi: UseOpenCloseReturn = actionContexts[key].modal;

  const [selectedUuid, setSelectedUuid] = useState<string>();

  const params = useMemo(() => {
    const includes = getDocumentIncludesForRelationshipObject(
      config.getWorkflowConfig().rootDocument.relationships,
    );
    return buildIncludeParams(includes);
  }, [config]);

  const { application } = useApplication(
    applicationUuid,
    { definition },
    params,
  );

  const { performGreenIdCheck, isPending } =
    useApplicationGreenIdCheck(definition);

  const people = useMemo<PeopleArray>(() => {
    if (!application) return [];
    const responseData = application as any;
    let responsePeople: PeopleArray = [] as PeopleArray;
    const { directors, individuals, shareholders, soleTrader } = responseData;

    if (directors) {
      responsePeople = responsePeople.concat(directors);
    }
    if (individuals) {
      responsePeople = responsePeople.concat(individuals);
    }
    if (shareholders) {
      responsePeople = responsePeople.concat(shareholders);
    }
    const trustees = responseData.individualTrustees;
    if (trustees) {
      responsePeople = responsePeople.concat(trustees);
    }
    const corporateTrustees = responseData.corporateTrusteeDirectors;
    if (corporateTrustees) {
      responsePeople = responsePeople.concat(corporateTrustees);
    }
    if (soleTrader) {
      responsePeople = responsePeople.concat([soleTrader]);
    }
    return responsePeople;
  }, [application]);

  const startIdCheckTransition = useEventHandler(
    async (uuid: string): Promise<void> => {
      try {
        const stateResponse = await performGreenIdCheck({
          uuid,
          data: {} as Entity,
        });
        const idCheckFailed = stateResponse?.data?.idCheckResult === 'failed';
        if (idCheckFailed) {
          handleError('ID check failed');
        } else {
          handleSuccess('ID check completed');
        }
        setLastApplicationUpdate(new Date());
        greenIdCheckModalApi.onClose();
      } catch (error: any) {
        handleError(
          error?.response?.data?.error?.message ??
            error?.message ??
            'ID check failed',
        );
      }
    },
  );

  return (
    <Dialog
      header="ID Check"
      onOpenChange={greenIdCheckModalApi.onOpenChange}
      open={greenIdCheckModalApi.open}
      size="lg"
      subHeader="Select a person to perform an ID check"
    >
      <Stack gap={4} p={6}>
        {people.map((person: Entity) => (
          <Flex
            key={`${person.definition}-${person.id}`}
            alignItems="center"
            bgColor={selectedUuid === person.id ? 'primary.500' : 'gray.100'}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="md"
            cursor="pointer"
            onClick={() => setSelectedUuid(person.id as string)}
            px={4}
            py={2}
          >
            <Text>
              {`${person.firstName}`} {`${person.lastName}`} -{' '}
              <Text as="span" textTransform="capitalize">
                {(person.definition as string).replaceAll('_', ' ')}
              </Text>
            </Text>
          </Flex>
        ))}

        <Stack direction="row" justifyContent="end">
          <Button
            colorPalette="gray"
            fontSize="md"
            fontWeight="normal"
            onClick={greenIdCheckModalApi.onClose}
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            colorPalette="primary"
            disabled={!selectedUuid}
            fontSize="md"
            fontWeight="bold"
            loading={isPending}
            onClick={() => startIdCheckTransition(selectedUuid || '')}
            variant="solid"
          >
            Check ID
          </Button>
        </Stack>
      </Stack>
    </Dialog>
  );
};
